import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";

const OutletCard = ({
  detailLink,
  outletNumber,
  outletName,
  connectionEstablished,
  onRefresh,
  onRefreshLabel = "Refresh outlet",
  onRename,
  onRenameLabel = "Rename outlet",
  metadata = [],
  metrics = [],
  hideMetrics = false,
  footerContent = null,
  connectionLabels = { online: "Connected", offline: "Disconnected" },
  className = "",
}) => {
  const connectionClassName = connectionEstablished
    ? "outlet-card__connection outlet-card__connection--online"
    : "outlet-card__connection outlet-card__connection--offline";
  const containerClassName = [
    "outlet-card",
    "show-for-small-only",
    connectionEstablished ? "" : "outlet-card--offline",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClassName}>
      <div className="outlet-card__header">
        <div className="outlet-card__identity">
          <Link to={detailLink} className="outlet-card__outlet">
            Outlet {outletNumber}
          </Link>
          <span className={connectionClassName}>
            {connectionEstablished
              ? connectionLabels.online
              : connectionLabels.offline}
          </span>
        </div>
        {onRefresh && (
          <div className="outlet-card__actions">
            <button
              type="button"
              className="outlet-card__icon-button"
              onClick={onRefresh}
              title={onRefreshLabel}
            >
              <i className="material-icons" aria-hidden="true">
                sync
              </i>
              <span className="sr-only">{onRefreshLabel}</span>
            </button>
          </div>
        )}
      </div>

      <div className="outlet-card__name-row">
        <Link to={detailLink} className="outlet-card__name">
          {outletName}
        </Link>
        {onRename && (
          <button
            type="button"
            className="outlet-card__icon-button"
            onClick={onRename}
            title={onRenameLabel}
          >
            <i className="material-icons" aria-hidden="true">
              edit
            </i>
            <span className="sr-only">{onRenameLabel}</span>
          </button>
        )}
      </div>

      {metadata.length > 0 && (
        <div className="outlet-card__meta">
          {metadata.map((field, index) => {
            const valueClassName = field.skipDefaultValueClass
              ? field.valueClassName || ""
              : field.valueClassName
              ? `outlet-card__value ${field.valueClassName}`
              : "outlet-card__value";

            return (
              <div className="outlet-card__field" key={field.key || index}>
                <span className="outlet-card__label">{field.label}</span>
                <div className={valueClassName}>{field.value}</div>
              </div>
            );
          })}
        </div>
      )}

      {!hideMetrics && metrics.length > 0 && (
        <div className="outlet-card__metrics">
          {metrics.map((field, index) => {
            const valueClassName = field.skipDefaultValueClass
              ? field.valueClassName || ""
              : field.valueClassName
              ? `outlet-card__value ${field.valueClassName}`
              : "outlet-card__value";

            return (
              <div className="outlet-card__field" key={field.key || index}>
                <span className="outlet-card__label">{field.label}</span>
                <div className={valueClassName}>{field.value}</div>
              </div>
            );
          })}
        </div>
      )}

      {footerContent}
    </div>
  );
};

OutletCard.propTypes = {
  detailLink: PropTypes.string.isRequired,
  outletNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  outletName: PropTypes.node.isRequired,
  connectionEstablished: PropTypes.bool,
  onRefresh: PropTypes.func,
  onRefreshLabel: PropTypes.string,
  onRename: PropTypes.func,
  onRenameLabel: PropTypes.string,
  metadata: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.node.isRequired,
      value: PropTypes.node.isRequired,
      valueClassName: PropTypes.string,
      skipDefaultValueClass: PropTypes.bool,
    })
  ),
  metrics: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.node.isRequired,
      value: PropTypes.node.isRequired,
      valueClassName: PropTypes.string,
      skipDefaultValueClass: PropTypes.bool,
    })
  ),
  hideMetrics: PropTypes.bool,
  footerContent: PropTypes.node,
  connectionLabels: PropTypes.shape({
    online: PropTypes.node,
    offline: PropTypes.node,
  }),
  className: PropTypes.string,
};

OutletCard.defaultProps = {
  connectionEstablished: true,
  onRefresh: null,
  onRename: null,
  metadata: [],
  metrics: [],
  hideMetrics: false,
  footerContent: null,
  connectionLabels: { online: "Connected", offline: "Disconnected" },
  className: "",
};

export default OutletCard;
